import { useState, useEffect } from "react";

export default function SlotMachineAnimation({ proposals, players, finalizeResult }) {
  const [currentHighlight, setCurrentHighlight] = useState(0);
  const [isSpinning, setIsSpinning] = useState(true);
  const [revealWinner, setRevealWinner] = useState(false);

  const tiedIds = finalizeResult.tied || [];
  const winnerIndex = tiedIds.indexOf(finalizeResult.winner);

  useEffect(() => {
    let spinCount = 0;
    const totalSpins = 18;
    let currentSpeed = 120;

    const spin = () => {
      if (spinCount >= totalSpins) {
        setCurrentHighlight(winnerIndex);
        setIsSpinning(false);
        setTimeout(() => setRevealWinner(true), 200);
        return;
      }
      if (spinCount > totalSpins - 6) currentSpeed = 250;
      else if (spinCount > totalSpins - 10) currentSpeed = 180;
      setCurrentHighlight((prev) => (prev + 1) % tiedIds.length);
      spinCount++;
      setTimeout(spin, currentSpeed);
    };

    const startDelay = setTimeout(spin, 400);
    return () => clearTimeout(startDelay);
  }, [tiedIds.length, winnerIndex]);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm">
      <div className="relative w-full max-w-lg rounded-2xl border border-amber-400/30 bg-gradient-to-b from-slate-950 to-slate-900 p-8 shadow-2xl">
        <div className="pointer-events-none absolute -top-24 -left-24 h-64 w-64 rounded-full bg-amber-500/20 blur-3xl animate-pulse" />
        <div className="pointer-events-none absolute -bottom-24 -right-24 h-64 w-64 rounded-full bg-orange-500/20 blur-3xl animate-pulse" />

        <div className="relative text-center mb-8">
          <p className="text-sm uppercase tracking-[0.2em] text-amber-200/80 mb-3 animate-fade-in">
            {finalizeResult.method === "tiebreaker" ? "⚖️ Égalité détectée !" : "🎲 Tirage au sort !"}
          </p>
          <h2 className="text-3xl font-extrabold text-white mb-2 animate-fade-in" style={{ animationDelay: "0.3s" }}>
            🎰 Tirage au sort
          </h2>
          <p className="text-sm text-slate-400 animate-fade-in" style={{ animationDelay: "0.6s" }}>
            {finalizeResult.method === "tiebreaker"
              ? `${tiedIds.length} noms à égalité`
              : `Tirage parmi ${tiedIds.length} propositions`}
          </p>
        </div>

        <div className="relative mb-8 rounded-2xl border-2 border-amber-400/40 bg-black/40 p-6 overflow-hidden">
          {isSpinning && (
            <div className="absolute inset-0 bg-gradient-to-b from-amber-400/20 via-transparent to-transparent animate-scroll-light pointer-events-none" />
          )}
          <div className="space-y-3">
            {tiedIds.map((playerId, index) => {
              const proposer = players.find(p => p.id === Number(playerId));
              const isHighlighted = currentHighlight === index;
              const isWinner = revealWinner && index === winnerIndex;
              return (
                <div key={playerId} className={[
                  "relative rounded-xl border px-5 py-4 transition-all duration-300",
                  isWinner ? "border-emerald-400 bg-emerald-500/30 scale-105 shadow-[0_0_40px_rgba(16,185,129,0.9)]"
                    : isHighlighted ? "border-amber-400 bg-amber-500/30 scale-102 shadow-[0_0_24px_rgba(251,191,36,0.6)]"
                    : "border-white/10 bg-white/5 opacity-50 scale-95",
                ].join(" ")}>
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className={["text-base font-bold mb-1 transition-colors",
                        isWinner ? "text-emerald-100" : isHighlighted ? "text-amber-100" : "text-slate-400"
                      ].join(" ")}>{proposals[playerId]}</p>
                      <p className="text-xs text-slate-400">Proposé par {proposer?.pseudo || "?"}</p>
                    </div>
                    {isWinner && (
                      <div className="flex items-center gap-3 animate-winner-pop">
                        <span className="text-3xl animate-bounce-slow">🎉</span>
                        <div className="px-4 py-2 rounded-full bg-emerald-400/30 border-2 border-emerald-400/70 shadow-[0_0_16px_rgba(16,185,129,0.6)]">
                          <span className="text-emerald-100 text-sm font-bold uppercase tracking-wider">Gagnant !</span>
                        </div>
                      </div>
                    )}
                    {isHighlighted && isSpinning && (
                      <div className="flex items-center gap-2">
                        <div className="h-3 w-3 rounded-full bg-amber-400 animate-ping" />
                        <div className="h-3 w-3 rounded-full bg-amber-400" />
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {isSpinning && (
            <div className="absolute top-4 right-4">
              <div className="px-3 py-1 rounded-full bg-amber-500/20 border border-amber-400/50 text-amber-200 text-xs font-bold uppercase tracking-wider animate-pulse">
                En cours...
              </div>
            </div>
          )}
        </div>

        {revealWinner && (
          <div className="relative text-center space-y-4 animate-fade-in-slow">
            <div className="rounded-xl border-2 border-emerald-400/50 bg-emerald-500/20 px-6 py-4 shadow-[0_0_24px_rgba(16,185,129,0.4)]">
              <p className="text-xs text-emerald-300/80 mb-2 uppercase tracking-wider">🏆 Nom d'équipe sélectionné</p>
              <p className="text-2xl font-extrabold text-emerald-100">{finalizeResult.teamName}</p>
            </div>
            <div className="flex items-center justify-center gap-2 text-sm text-slate-400">
              <div className="h-2 w-2 rounded-full bg-emerald-400 animate-ping" />
              <span>Lancement de la partie...</span>
            </div>
          </div>
        )}

        <style>{`
          @keyframes fade-in-slow { from { opacity:0; transform:scale(0.95) translateY(-10px); } to { opacity:1; transform:scale(1) translateY(0); } }
          .animate-fade-in-slow { animation: fade-in-slow 0.8s ease-out forwards; opacity:0; }
          @keyframes fade-in { from { opacity:0; transform:translateY(-10px); } to { opacity:1; transform:translateY(0); } }
          @keyframes winner-pop { 0% { opacity:0; transform:scale(0.5) rotate(-10deg); } 60% { transform:scale(1.15) rotate(5deg); } 100% { opacity:1; transform:scale(1) rotate(0deg); } }
          @keyframes bounce-slow { 0%,100% { transform:translateY(0); } 50% { transform:translateY(-8px); } }
          @keyframes scroll-light { 0% { transform:translateY(-100%); } 100% { transform:translateY(100%); } }
          .animate-fade-in { animation: fade-in 0.6s ease-out forwards; opacity:0; }
          .animate-winner-pop { animation: winner-pop 0.6s ease-out forwards; opacity:0; }
          .animate-bounce-slow { animation: bounce-slow 1s ease-in-out infinite; }
          .animate-scroll-light { animation: scroll-light 1.5s linear infinite; }
        `}</style>
      </div>
    </div>
  );
}